import { Card, CardContent, Skeleton } from '@billda/design-system';
import { ArrowForwardIcon } from '@billda/design-system';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Routes } from '../../../routes';
import { useGetPotStatusSummary } from '../hooks/useGetPotStatusSummary';
import { useGetDashboardPeriodInfo } from '../hooks/useGetDashboardPeriodInfo';
import { formatCurrency } from '../../../shared/utils/currency';
import type { PotStatusSummaryDto } from '../api/dashboardApi';
import {
  getStatusColor,
  getStatusLabel,
  buildFullStatusList,
  filterDisplayStatuses,
} from '../utils/potStatusConfig';
import { formatPeriodSubtitle } from '../utils/periodFormatting';

export function PotStatusOverview() {
  const { data, isLoading } = useGetPotStatusSummary();
  const { data: periodInfo } = useGetDashboardPeriodInfo();

  const periods = periodInfo?.periods ?? [];
  const subtitle = formatPeriodSubtitle(periods);

  const allStatuses = buildFullStatusList(data?.statuses ?? []);
  const chartStatuses = filterDisplayStatuses(data?.statuses ?? []).filter(
    s => s.count > 0
  );
  const totalCount = data?.totalCount ?? 0;

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <Card data-testid="pot-status-overview" className="min-w-0 flex-1">
      <CardContent className="p-6">
        {/* Header */}
        <div className="mb-6">
          <p className="type-body-lg font-bold">Pot Status Overview</p>
          {subtitle ? (
            <p className="mt-0.5 type-body-md text-content-secondary">
              {subtitle}
            </p>
          ) : null}
        </div>

        {/* Chart + Legend */}
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:gap-8">
          {/* Donut chart */}
          <div
            className="relative size-40 shrink-0"
            data-testid="pot-status-chart"
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={0}
              minHeight={0}
              initialDimension={{ width: 160, height: 160 }}
            >
              <PieChart>
                {chartStatuses.length > 0 ? (
                  <Pie
                    data={chartStatuses}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={75}
                    strokeWidth={2}
                    stroke="transparent"
                    paddingAngle={1}
                  >
                    {chartStatuses.map(entry => (
                      <Cell
                        key={entry.status}
                        fill={getStatusColor(entry.status)}
                      />
                    ))}
                  </Pie>
                ) : (
                  <Pie
                    data={[{ value: 1 }]}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={75}
                    strokeWidth={0}
                  >
                    <Cell fill="rgba(255,255,255,0.08)" />
                  </Pie>
                )}
              </PieChart>
            </ResponsiveContainer>
            {/* Centre label */}
            <div
              className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center"
              data-testid="pot-status-total"
            >
              <p className="type-h5 leading-[1.1] font-bold">{totalCount}</p>
              <p className="type-caption tracking-[0.02em] text-content-secondary">
                Pots
              </p>
            </div>
          </div>

          {/* Legend */}
          <div className="min-w-0 w-full flex-1">
            <div className="flex flex-col gap-0.75">
              {allStatuses.map(entry => (
                <StatusRow key={entry.status} entry={entry} />
              ))}
            </div>
          </div>
        </div>

        {/* View All Pots link */}
        <div className="mt-6">
          <Link
            to={Routes.finance.pots}
            data-testid="view-all-pots-link"
            className="inline-flex items-center gap-0.5 type-body-md font-medium text-content-warning no-underline hover:underline"
          >
            View All Pots
            <ArrowForwardIcon aria-hidden="true" className="size-3.5" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusRow({ entry }: { entry: PotStatusSummaryDto }) {
  const isEmpty = entry.count === 0;

  return (
    <div
      className="flex items-center gap-1.5 py-0.25"
      data-testid={`status-row-${entry.status}`}
    >
      {/* Colour dot */}
      <span
        className={`size-2.5 shrink-0 rounded-full ${isEmpty ? 'opacity-30' : ''}`}
        style={{ backgroundColor: getStatusColor(entry.status) }}
      />
      {/* Label */}
      <span
        className={`type-body-md min-w-0 flex-1 ${isEmpty ? 'text-content-secondary' : ''}`}
      >
        {getStatusLabel(entry.status)}
      </span>
      {/* Count */}
      <span
        className={`type-body-md min-w-7 text-right font-semibold tabular-nums ${isEmpty ? 'text-content-secondary' : ''}`}
      >
        {entry.count}
      </span>
      {/* Gross value */}
      <span className="type-body-md min-w-20 text-right tabular-nums text-content-secondary">
        {formatCurrency(entry.grossValue)}
      </span>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <Card data-testid="pot-status-overview-loading" className="min-w-0 flex-1">
      <CardContent className="p-6">
        <Skeleton width={180} height={24} className="mb-0.5" />
        <Skeleton width={160} height={18} className="mb-6" />
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:gap-8">
          <Skeleton variant="circular" width={160} height={160} />
          <div className="w-full flex-1">
            <div className="flex flex-col gap-0.75">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} height={24} />
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
